/**
 * Parcours installateur — le SEUL écran traduit de l'application.
 *
 * Les poseurs ne sont pas tous francophones et signent des mandats chez des clients :
 * c'est la raison d'être des cinq langues. Le reste de l'application reste en français,
 * parce que ses utilisateurs le sont, et montrer un sélecteur ailleurs promettrait une
 * traduction qui n'existe pas.
 *
 * ⚠️ Couverture actuelle : navigation, rôle, accueils et gestion d'équipe. Les
 * libellés de champs du parcours de signature restent en français ; le dictionnaire
 * les porte déjà, c'est le câblage qui reste à faire.
 */
import { useState, useEffect } from 'react';
import { useAuth } from '@/shared/auth/AuthContext';
import api from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/shared/ui/dialog';
import { Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { compressImage } from '@/shared/utils/image';
import StepClientInfo from '@/features/installer/components/wizard/StepClientInfo';
import StepEquipement, { etiquettesPhotos } from '@/features/installer/components/wizard/StepEquipement';
import { UNITE_RADIATEUR_INIT, etiquettePhotoRadiateur }
  from '@/features/installer/components/wizard/equipements/BlocsSimples';
import StepOffre, { OFFRE_ADHESION, OFFRE_REFUS } from '@/features/installer/components/wizard/StepOffre';
import StepFinChantier from '@/features/installer/components/wizard/StepFinChantier';
import StepConfirmation from '@/features/installer/components/wizard/StepConfirmation';
import MesChantiers from '@/features/installer/components/wizard/MesChantiers';
import useLangue from '@/shared/i18n/useLangue';
import NavigationInstallateur from '@/features/installer/components/nav/NavigationInstallateur';
import AccueilPoseur from '@/features/installer/components/home/AccueilPoseur';
import AccueilInstallateur from '@/features/installer/components/home/AccueilInstallateur';
import MonEquipe from '@/features/installer/components/equipe/MonEquipe';
import { PoseurDialog, ConfirmationPoseur } from '@/features/installer/components/equipe/PoseurDialogs';
import { depasseLaBorne, useRefusDuServeur } from '@/features/installer/utils/refusIdentifiant';

// L'ORDRE DU PARCOURS, arbitre le 30/08 par David. IL EN REVOQUE UN AUTRE.
//
// Le 27/08, toujours sur sa demande, le lot C avait eclate l'ecran de signature unique
// en quatre etapes — Mandat (4), Fin de chantier (5), Consentements (6), Confirmation
// (7) — avec DEUX signatures distinctes. Ce n'etait pas un hasard : le client donnait
// ses accords APRES avoir signe la fin de ses travaux, et un seul trait de stylo valait
// pour deux engagements juridiques, donc n'en prouvait aucun.
//
// Le 30/08 au soir, David a demande l'inverse : le mandat et les consentements
// DESCENDENT sur la page de fin de chantier, et UNE SEULE signature couvre l'ensemble.
// Cette demande remplace celle du 27/08.
//
// ⚠️ CE N'EST PAS UN RETOUR A L'ANCIEN ECRAN. La critique de juillet tenait a l'ORDRE
// de lecture : le client signait la reception avant d'avoir lu ses autorisations. Ici
// il lit le mandat, puis les autorisations, puis ce qu'il receptionne, et il signe en
// DERNIER, en bas de la meme page. Le texte qui accompagne la signature nomme les trois
// actes, et le serveur ECRIT ce qu'elle couvre (`signature_portee`) pour que les
// dossiers a deux signatures des 27-30 aout restent lisibles tels qu'ils ont ete signes.
//
// L'appel au serveur reste UNIQUE et part de l'etape 4 : les consentements y sont
// exiges avant toute ecriture, et un chantier ne doit pas pouvoir exister a moitie.
const STEPS = [
  { id: 1, label: 'Client' },
  { id: 2, label: 'Actif' },
  { id: 3, label: 'Offre' },
  { id: 4, label: 'Fin de chantier' },
  { id: 5, label: 'Confirmation' },
];

const EMPTY_CLIENT = {
  civilite: 'M.', nom: '', prenom: '', adresse: '', complement_adresse: '',
  code_postal: '', ville: '',
  region: '', departement: '', pdl_prm: '', email: '', telephone: '',
  // Informations logement
  type_logement: '', mode_chauffage: '', surface_m2: '',
};
const todayISO = () => new Date().toISOString().slice(0, 10);

// Nouveau modele par equipement (un seul modele global, certains champs sont type-specifiques)
const EQUIPEMENTS_INIT = {
  // Pour le PV, il n'y a plus de « marque » generique : ce qui compte est la marque du
  // PANNEAU, qui remonte ensuite comme marque de l'actif.
  PV: {
    enabled: false, puissance_kw: '',
    panneau_marque: '', panneau_modele: '',
    nombre_panneaux: '', puissance_panneau_wc: '',
    type_onduleur: 'microonduleur', onduleur_id: '', onduleur_marque: 'Hoymiles',
    onduleur_modele: '', onduleur_serial: '',
    date_installation: todayISO(), photos_serial: [],
    // Verdict de la derniere verification materielle, avec l'identifiant sur
    // lequel il porte. Null tant que rien n'a ete verifie.
    verification: null,
  },
  Batterie: {
    // Le modele n'est plus pre-rempli en dur. « HiBattery 4020 X » etait faux sur la
    // seule batterie reelle du parc, qui s'annonce « HiBattery 1920 AC » dans sa
    // decouverte Home Assistant, et personne ne pouvait s'en apercevoir puisque la
    // valeur etait ecrite d'avance. C'est la verification qui le remplit desormais.
    enabled: false, puissance_kw: '', marque: '', modele: '',
    reference: '', identifiant_equipement: '', contrat_oa: false,
    date_installation: todayISO(), photos_serial: [],
    // Verdict de la derniere verification materielle, avec l'identifiant sur
    // lequel il porte. Null tant que rien n'a ete verifie.
    verification: null,
  },
  // `technologie` decide du profil de flexibilite : resistif et thermodynamique ne
  // valent pas la meme chose, et survoltage.py les separe deja.
  Ballon: {
    enabled: false, nombre_ballons: '1', marque: '', reference: '',
    identifiant_equipement: '', technologie: 'resistif', date_installation: todayISO(), photos_serial: [],
    verification: null,
  },
  PAC: {
    enabled: false, puissance_kw: '', marque: '', modele: '', reference: '',
    identifiant_equipement: '', numero_serie_interieur: '', numero_serie_exterieur: '',
    thermostat_reference: '', thermostat_serial: '', date_installation: todayISO(), photos_serial: [],
    verification: null,
  },
  // UN RADIATEUR SAISI = UN RADIATEUR POSE, depuis le 28/08/2026.
  //
  // Le bloc portait un seul jeu de champs et un compteur « Nombre de radiateurs », donc
  // UN seul identifiant Tuya pour toute la maison. Une maison en porte plusieurs, chacun
  // avec son module de pilotage : le parc reel n'etait pas representable, donc ni
  // pilotable ni valorisable. Chaque appareil vit desormais dans `unites`, avec son
  // identifiant, son verdict de verification et sa photo d'etiquette.
  //
  // `technologie` a disparu : R2 n'est pas une technologie de radiateur, c'est un
  // mecanisme de remuneration. Voir la note en tete de BlocsSimples.jsx.
  // Le RADIATEUR n'est pas ici : sa valeur initiale porte un tableau, elle se
  // reconstruit a chaque fois. Voir `radiateurVierge` juste en dessous.
};

// LA LISTE DES RADIATEURS SE RECONSTRUIT, ELLE NE SE RECOPIE PAS.
//
// Les autres types sont des objets plats : `{ ...EQUIPEMENTS_INIT.PV }` en donne une
// copie sure. Un radiateur porte un TABLEAU d'appareils ; une copie de surface le
// partagerait entre le formulaire courant et le suivant. Et `todayISO()` fige la date
// au chargement du module : un poseur qui laisse l'onglet ouvert la nuit repartirait
// avec la date de la veille.
const radiateurVierge = () => ({
  enabled: false, photos_serial: [],
  unites: [{ ...UNITE_RADIATEUR_INIT, date_installation: todayISO() }],
});

// LES TROIS AUTORISATIONS, toutes a false, et elles seules depuis le 02/09/2026.
//
// `cgv`, `rgpd` et `exclusivite` vivaient ici. Boubacar a fait retirer les trois cases
// de l'ecran ; elles ont quitte `CONSENT_KEYS` cote serveur en meme temps, sans quoi le
// mandat aurait enregistre un REFUS sur trois clauses qu'on ne pose plus.
//
// ⚠️ NE PAS EN LAISSER UNE ICI « au cas ou ». `step4Valid` exige que TOUTES les valeurs
// de cet objet soient vraies : une cle orpheline qu'aucune case ne coche eteindrait le
// bouton « Terminer le chantier » pour toujours, devant le client.
const EMPTY_CONSENTS = { modulation: false, donnees: false, audit: false };

export default function InstallateurPage() {
  const { langue, changerLangue, t } = useLangue();
  const { user, logout } = useAuth();
  // Le poseur arrive sur ses chiffres, pas au milieu d'un formulaire.
  const [view, setView] = useState('home'); // 'home' | 'wizard' | 'mes-chantiers' | 'equipe'
  const isPoseur = user?.role === 'poseur';
  const [homeStats, setHomeStats] = useState(null);
  const [poseurStats, setPoseurStats] = useState(null);
  const [poseurs, setPoseurs] = useState([]);
  const [poseurDialog, setPoseurDialog] = useState(null); // { mode: 'create'|'edit', data }
  const [poseurSaving, setPoseurSaving] = useState(false);
  const [poseurConfirm, setPoseurConfirm] = useState(null); // { poseur, geste }
  const [step, setStep] = useState(1);

  // CHANGER D'ETAPE REMONTE LA PAGE.
  //
  // Releve par la mesure responsive du 24/08, en 390 px : a l'arrivee sur l'etape 2,
  // le titre « Equipement installe » se trouvait SOUS l'en-tete `sticky`. La cause
  // n'est pas l'en-tete, qui joue son role, mais le fait que le passage d'une etape a
  // l'autre conservait la position de defilement de la precedente. Le poseur validait
  // une etape en bas d'ecran et arrivait sur la suivante au milieu, sans en voir le
  // titre — debout chez un client, sur un ecran de 390 px, c'est une etape entiere
  // qu'on croit avoir sautee.
  //
  // Le defaut ne se voit pas sur un ecran de bureau : la page y tient sans defilement.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [step]);
  const [error, setError] = useState('');
  const [client, setClient] = useState({ ...EMPTY_CLIENT });
  const [equipements, setEquipements] = useState({
    PV: { ...EQUIPEMENTS_INIT.PV },
    Batterie: { ...EQUIPEMENTS_INIT.Batterie },
    Ballon: { ...EQUIPEMENTS_INIT.Ballon },
    PAC: { ...EQUIPEMENTS_INIT.PAC },
    Radiateur: radiateurVierge(),
  });
  const [referenceChantier, setReferenceChantier] = useState('');
  // Une référence PROPOSÉE, jamais imposée — arbitrage du 31/08, en réponse à
  // « comment l'installateur définira la référence chantier ? ».
  //
  // Elle reste un champ libre : chaque installateur a sa propre nomenclature, souvent
  // celle de son devis ou de son logiciel de gestion, et lui en imposer une l'obligerait
  // à tenir deux références pour un seul chantier. Mais un champ vide devant un client
  // fait inventer sur place, et c'est ainsi qu'on obtient « TEST » ou « aaa ».
  //
  // La proposition se déclenche à l'entrée sur l'étape 2 et seulement si le champ est
  // encore vide : elle ne réécrit jamais une saisie, ni au retour en arrière.
  useEffect(() => {
    if (step !== 2 || referenceChantier) return;
    const nom = (client?.nom || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 6);
    const d = new Date();
    const jour = `${String(d.getFullYear()).slice(2)}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
    if (nom) setReferenceChantier(`${nom}-${jour}`);
  }, [step, referenceChantier, client?.nom]);
  const [consents, setConsents] = useState({ ...EMPTY_CONSENTS });
  const [agregateur, setAgregateur] = useState(''); // C8a, voir ChoixAgregateurMandat
  // ⚠️ `consentPdl` VIVAIT ICI, ET SON RETRAIT EST VOULU (10/09/2026).
  //
  // Il portait la case de consentement d'acces aux donnees du compteur, separee des
  // consentements du mandat parce qu'elle ne s'adresse pas au meme tiers : ceux-la
  // engagent l'operateur, ses operateurs partenaires et RTE, celle-ci le gestionnaire de
  // reseau via Energrid. Boubacar a fait sauter la case de l'ecran ; l'etat qui la
  // suivait n'a plus rien a suivre.
  //
  // Le consentement, lui, part toujours -- voir `handleSign` plus bas, qui porte le
  // pourquoi et ce qui en porte desormais la preuve.
  // `recherchePdl` vivait ici et partait dans la requete. Il valait TOUJOURS `false` :
  // aucune case du parcours terrain ne l'a jamais coche, et le serveur lisait de son
  // cote `consents.recherche_pdl`, que cet ecran n'envoie pas non plus. La case « PDL
  // introuvable » n'existait que dans le CRM, d'ou elle a ete retiree le 02/09.
  const [offre, setOffre] = useState(null);
  // La signature du MANDAT (etape 4). Le nom reste celui du champ serveur.
  const [signatureData, setSignatureData] = useState('');
  // Celle de l'ATTESTATION DE FIN DE TRAVAUX (etape 5). Deux actes juridiques
  // distincts : un seul trait de stylo pour les deux ne prouvait ni l'un ni l'autre.
  // ⚠️ `signatureOtp` VIVAIT ICI, ET SA DISPARITION EST LE COEUR DU 03/09/2026.
  //
  // Il portait la demande de signature A DISTANCE en cours : non nul, le poseur restait
  // dans l'etape 4 sur un ecran d'attente ou de saisie de code, en attendant que le
  // client signe une SECONDE fois chez Yousign. Boubacar a retire ce canal — « on ne
  // garde que la signature sur le canvas apparaissant a l'ecran ». Le client signe une
  // fois, devant le poseur, et l'etape 4 mene directement a la confirmation.
  const [signing, setSigning] = useState(false);
  const [result, setResult] = useState(null);
  const [satisfaction, setSatisfaction] = useState({
    chantier_propre: { answer: '', comment: '' },
    equipe_pose: { answer: '', comment: '' },
    emplacement: { answer: '', comment: '' },
    satisfait_installation: { answer: '', comment: '' },
  });
  const [photos, setPhotos] = useState([]);
  const [profileOpen, setProfileOpen] = useState(false);
  // L'adresse de l'installateur part en en-tete des attestations de fin de travaux
  // remises au client. Sans code postal ni ville elle etait incomplete : un document
  // remis a un particulier portait une rue et rien d'autre.
  const [profile, setProfile] = useState({
    company_name: '', address: '', code_postal: '', ville: '', phone: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [menuData, setMenuData] = useState({ regions: [], types_actifs: [], civilites: [] });
  // Mes chantiers
  const [chantiers, setChantiers] = useState([]);
  const [chantiersLoading, setChantiersLoading] = useState(false);
  // Les chantiers ouverts dont la signature n'est pas revenue. Liste SEPAREE de
  const [chantierSearch, setChantierSearch] = useState('');

  useEffect(() => {
    api.get('/admin/menus').then(r => setMenuData(r.data)).catch(() => {});
    // Charger le profil installateur
    api.get('/auth/me').then(r => {
      setProfile({
        company_name: r.data.company_name || '',
        address: r.data.address || '',
        code_postal: r.data.code_postal || '',
        ville: r.data.ville || '',
        phone: r.data.phone || '',
      });
    }).catch(() => {});
  }, []);

  // Les accueils et l'equipe se chargent a la demande : un poseur ne declenche jamais
  // l'appel installateur, et inversement. Les routes le refuseraient de toute facon,
  // mais un 403 dans la console fait douter d'une panne qui n'existe pas.
  useEffect(() => {
    if (view !== 'home') return;
    if (isPoseur) {
      api.get('/poseur/home-stats').then(r => setPoseurStats(r.data)).catch(() => {});
    } else {
      api.get('/installateur/home-stats').then(r => setHomeStats(r.data)).catch(() => {});
    }
  }, [view, isPoseur]);

  useEffect(() => {
    if (view === 'equipe' && !isPoseur) {
      api.get('/installateur/poseurs').then(r => setPoseurs(r.data || [])).catch(() => {});
    }
  }, [view, isPoseur]);

  const rechargerPoseurs = () =>
    api.get('/installateur/poseurs').then(r => setPoseurs(r.data || [])).catch(() => {});

  // Charger Mes chantiers a la demande
  useEffect(() => {
    if (view === 'mes-chantiers') {
      setChantiersLoading(true);
      api.get('/installateur/mes-chantiers')
        .then(r => setChantiers(r.data || []))
        .catch(() => setChantiers([]))
        .finally(() => setChantiersLoading(false));
    }
  }, [view]);

  const downloadAttestation = async (mandatId, clientNom) => {
    try {
      const res = await api.get(`/mandats/${mandatId}/attestation-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `attestation_${clientNom || mandatId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (erreur) {
      // UN CLIC QUI NE FAIT RIEN EST PIRE QU UNE ERREUR. Ce `catch` etait vide : le
      // bouton s affichait, l appel repondait 404 parce que le chantier de son poseur
      // sortait du perimetre du telechargement, et il ne se passait RIEN. Ni message,
      // ni trace, rien a raconter au support. Le perimetre est repare par ailleurs ;
      // ce qui reste ici, c est de ne plus jamais avaler l issue en silence.
      const statut = erreur?.response?.status;
      toast.error(
        statut === 404
          ? "Ce document n'est pas accessible depuis ce compte."
          : statut === 409
            ? "Ce chantier n'a pas d'attestation : il a ete cree sans reception de "
              + 'travaux.'
            : "Le telechargement n'a pas abouti. Reessayez dans un instant.",
      );
    }
  };

  const chantiersFiltered = chantiers.filter(ch => {
    if (!chantierSearch.trim()) return true;
    const q = chantierSearch.toLowerCase();
    return [ch.client_nom, ch.client_prenom, ch.adresse, ch.code_postal, ch.ville, ch.reference_chantier]
      .filter(Boolean).some(s => String(s).toLowerCase().includes(q));
  });
  const fmtDate = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return iso; }
  };

  const up = (key, val) => setClient(c => ({ ...c, [key]: val }));
  // `val` peut etre une FONCTION de l'ancienne valeur, et ce n'est pas du confort.
  //
  // `ChampIdentifiant` ecrit TROIS fois de suite quand le materiel repond : le verdict,
  // puis la marque, puis le modele. Sur un champ plat les trois portent des cles
  // differentes et se composent. Sur la liste des radiateurs elles portent toutes la
  // meme cle `unites`, et trois valeurs calculees depuis le MEME etat de depart
  // s'ecrasaient : le verdict disparaissait, l'etape restait verrouillee sur un
  // appareil qui avait pourtant repondu. Mesure du 28/08 en recette navigateur.
  const upE = (type, key, val) => setEquipements(e => ({
    ...e,
    [type]: { ...e[type], [key]: typeof val === 'function' ? val(e[type][key]) : val },
  }));
  const toggleE = (type) => setEquipements(e => ({ ...e, [type]: { ...e[type], enabled: !e[type].enabled } }));
  const refusNumero = useRefusDuServeur(equipements, setError); // lot L4 : le 409 qui nomme un équipement

  // PDL validation
  const pdlDigits = (client.pdl_prm || '').replace(/\D/g, '');
  const pdlValid = pdlDigits.length === 0 || pdlDigits.length === 14;
  const pdlError = client.pdl_prm && !pdlValid ? 'Le numero PDL doit contenir exactement 14 chiffres' : '';

  // Validations
  const step1Valid = client.nom && client.prenom && client.adresse && client.code_postal
    && client.ville && client.email && client.telephone && client.pdl_prm && pdlValid
    && client.type_logement && client.mode_chauffage;

  // Construit la liste effective des actifs actives + valide chaque equipement
  const enabledEquipements = Object.entries(equipements).filter(([, v]) => v.enabled);
  // Une etiquette photographiee est la seule preuve qu'un numero saisi correspond a la
  // machine posee. Sans elle, un chiffre mal recopie ne se decouvre qu'au moment ou la
  // supervision ne trouve pas l'equipement — des semaines plus tard, chez le client.
  const photosRequisesPresentes = (type, v) => {
    const attendues = etiquettesPhotos(type, v);
    const prises = new Set((v.photos_serial || []).map((p) => p.label));
    return attendues.every((etiquette) => prises.has(etiquette));
  };

  // Un identifiant SAISI, quel qu'en soit le verdict. C'est tout ce que l'etape exige
  // desormais.
  //
  // ⚠️ ELLE EXIGEAIT DAVANTAGE, ET SA LEVEE EST LA DECISION DU LOT R. Depuis le lot D,
  // `identifiantVerifie` reclamait un verdict VIVANT sur l'identifiant courant : un
  // poseur dont l'appareil ne repondait pas ne depassait pas la deuxieme etape. Il ne
  // voyait ni l'offre, ni le mandat, ni la fin de chantier, et son dossier n'existait
  // nulle part. David a tranche le 30/08 : le poseur tape ce qu'il lit sur l'etiquette,
  // le produit lui DIT ce qu'il en pense, et le laisse continuer dans tous les cas.
  //
  // ⚠️ CE QUI N'EST PAS LEVE : la SAISIE. Un actif sans identifiant du tout ne serait
  // rattrapable par aucun des trois canaux — ni Tuya ni le broker n'auraient de quoi le
  // reconnaitre, et l'ecran de rattrapage n'aurait rien a comparer. On retire la porte,
  // pas le champ.
  //
  // ⚠️ ET LA MESURE RESTE. `ChampIdentifiant` continue d'interroger les deux canaux, de
  // rendre le verdict et de rappeler ce qui a ete tape avec son nombre de caracteres —
  // c'est le seul moment ou le poseur peut encore comparer a l'etiquette. Le serveur
  // refait la verification a la signature, l'ecrit sur l'actif, et met le chantier en
  // attente de materiel : voir `services/enrolement.trace_enrolement`.
  const identifiantSaisi = (type, v) =>
    ((type === 'PV' ? v.onduleur_id : v.identifiant_equipement) || '').trim().length > 0;

  const equipementValid = (type, v) => {
    if (!v.enabled) return true;
    if (!photosRequisesPresentes(type, v)) return false;
    // UN RADIATEUR SAISI = UN RADIATEUR POSE : chaque appareil porte son propre
    // identifiant, donc sa propre exigence. Un seul champ rempli pour trois machines
    // laisserait deux d'entre elles sans rien a rattraper.
    if (type === 'Radiateur') {
      const unites = v.unites || [];
      return unites.length > 0 && unites.every((u) => identifiantSaisi(type, u)
        && u.marque && u.reference && u.date_installation);
    }
    if (!identifiantSaisi(type, v)) return false;
    // Ce qui est exige est ce sans quoi l'equipement ne peut ni etre identifie ni
    // classe : un PV sans numero de serie d'onduleur ne remontera aucune telemetrie,
    // un ballon sans technologie ne peut pas etre range dans un profil.
    if (type === 'PV') {
      // ⚠️ `onduleur_id` ET `onduleur_serial` NE SONT PLUS EXIGES TOUS LES DEUX —
      // relevé de Boubacar du 31/08 : « il ne faut pas que les ID central Hoymiles
      // ou le numéro de série soit bloquants ».
      //
      // Le LOT R avait déjà fait tomber la porte du VERDICT : un identifiant qui ne
      // répond pas n'arrête plus le chantier. Ce qui bloquait encore était l'étoile
      // du formulaire — deux champs obligatoires sur une étiquette souvent illisible,
      // parfois posée en hauteur, et dont l'un des deux suffit à retrouver la machine.
      //
      // CE QUI RESTE EXIGÉ : l'un des deux, et `identifiantSaisi` le garantit plus
      // haut. Un PV sans AUCUN identifiant n'a aucun point d'accroche : ni la
      // supervision ni le rattrapage matériel ne pourraient jamais le retrouver, et
      // on recréerait le dossier fantôme que le rattrapage existe pour éviter.
      return v.puissance_kw && v.panneau_marque && v.panneau_modele && v.type_onduleur
        && v.nombre_panneaux && v.puissance_panneau_wc && v.date_installation;
    }
    if (type === 'Batterie') {
      return v.puissance_kw && v.marque && v.modele && v.reference
        && v.identifiant_equipement && v.date_installation;
    }
    if (type === 'Ballon') {
      return v.nombre_ballons && v.marque && v.reference && v.identifiant_equipement
        && v.technologie && v.date_installation;
    }
    if (type === 'PAC') {
      return v.puissance_kw && v.marque && v.modele && v.reference
        && v.identifiant_equipement && v.date_installation;
    }
    return true;
  };
  const step2Valid = enabledEquipements.length > 0
    && referenceChantier.trim().length > 0
    && enabledEquipements.every(([t, v]) => equipementValid(t, v)) && !depasseLaBorne(enabledEquipements);

  const allConsents = Object.values(consents).every(Boolean);
  const allSatisfaction = Object.values(satisfaction).every(s => s.answer === 'Oui' || s.answer === 'Non');
  // UNE page, UNE porte, UNE signature. Les trois conditions du 27/08 (mandat signe,
  // reception signee, consentements coches) tenaient trois etapes ; elles tiennent
  // desormais un seul bouton, parce que le client ne signe qu'une fois et qu'il signe
  // en dernier.
  //
  // Le bouton reste desactive tant qu'il manque quelque chose, et c'est volontaire :
  // le serveur refuse un mandat dont un consentement manque, et laisser partir l'appel
  // pour recevoir un 400 ferait echouer un chantier devant le client.
  const step4Valid = allSatisfaction && allConsents && !!signatureData;

  // Un equipement au moins n'a-t-il pas repondu ? Le poseur doit le savoir AVANT de
  // signer, pas au prochain audit : la page de fin de chantier le lui dit.
  const materielEnAttente = enabledEquipements.some(([type, v]) => {
    const verdictVivant = (u) => !!u.verification && u.verification.vivant === true
      && u.verification.identifiant
         === ((type === 'PV' ? u.onduleur_id : u.identifiant_equipement) || '').trim();
    if (type === 'Radiateur') return (v.unites || []).some((u) => !verdictVivant(u));
    return !verdictVivant(v);
  });

  // Transforme UN equipement saisi en UN actif du payload backend.
  // Les champs propres a un type sont mis a null pour les autres : un `contrat_oa` pose
  // sur une PAC n'a aucun sens, et le backend les retire avant enregistrement.
  const actifDepuis = (type, v) => ({
    type,
    marque: type === 'PV' ? (v.panneau_marque || '') : (v.marque || ''),
    modele: type === 'PV'
      ? (v.panneau_modele || 'PV')
      : (v.modele || (type === 'Ballon' ? 'ECS' : type === 'Radiateur' ? 'Radiateur' : '')),
    puissance_kw: parseFloat(v.puissance_kw) || 0,
    // L'ORDRE COMPTE, et il a change le 27/08 : pour un PV, l'ID central passe AVANT
    // le numero de serie de l'onduleur. C'est ce champ qui devient
    // `identifiant_equipement`, donc la cle par laquelle toute la telemetrie se
    // rattache — et un numero d'onduleur change au premier remplacement.
    identifiant_equipement: v.identifiant_equipement
      || (type === 'PV' ? (v.onduleur_id || v.onduleur_serial) : v.onduleur_serial)
      || v.onduleur_id || v.reference || '',
    reference: v.reference || '',
    thermostat_reference: v.thermostat_reference || '',
    thermostat_serial: v.thermostat_serial || '',
    nombre_ballons: type === 'Ballon' ? (parseInt(v.nombre_ballons, 10) || 1) : null,
    nombre_radiateurs: type === 'Radiateur' ? (parseInt(v.nombre_radiateurs, 10) || 1) : null,
    // Le radiateur n'en porte plus : R2 etait un mecanisme de remuneration range parmi
    // des technologies. Seul le ballon a une vraie technologie (resistif contre
    // thermodynamique), et survoltage.py les separe deja.
    technologie: type === 'Ballon' ? (v.technologie || null) : null,
    contrat_oa: type === 'Batterie' ? !!v.contrat_oa : null,
    type_onduleur: type === 'PV' ? (v.type_onduleur || null) : null,
    onduleur_id: type === 'PV' ? (v.onduleur_id || null) : null,
    onduleur_marque: type === 'PV' ? (v.onduleur_marque || null) : null,
    onduleur_modele: type === 'PV' ? (v.onduleur_modele || null) : null,
    onduleur_serial: type === 'PV' ? (v.onduleur_serial || null) : null,
    nombre_panneaux: type === 'PV' ? (parseInt(v.nombre_panneaux, 10) || null) : null,
    puissance_panneau_wc: type === 'PV' ? (parseInt(v.puissance_panneau_wc, 10) || null) : null,
    panneau_marque: type === 'PV' ? (v.panneau_marque || null) : null,
    panneau_modele: type === 'PV' ? (v.panneau_modele || null) : null,
    numero_serie_interieur: type === 'PAC' ? (v.numero_serie_interieur || null) : null,
    numero_serie_exterieur: type === 'PAC' ? (v.numero_serie_exterieur || null) : null,
    photos_serial: v.photos_serial || [],
    date_installation: v.date_installation || todayISO(),
    reference_chantier: referenceChantier,
  });

  // TROIS RADIATEURS POSES DOIVENT PRODUIRE TROIS ACTIFS, pas un actif qui en compte
  // trois. La telemetrie et le pilotage se rattachent a un identifiant, et il y en a un
  // par machine : un actif unique portant `nombre_radiateurs: 3` n'en laissait piloter
  // qu'un seul. Le serveur accepte deja N actifs de meme type — c'est le chemin
  // `extra_assets` de `POST /installateur/sign-complete`, sans contrainte d'unicite.
  //
  // `nombre_radiateurs` reste envoye, a 1 par actif : l'export distributeur et la fiche
  // SAV le lisent, et un champ qui disparait d'un coup casse des ecrans qui ne sont pas
  // de ce lot.
  const buildAssetsPayload = () => enabledEquipements.flatMap(([type, v]) => {
    if (type !== 'Radiateur') return [actifDepuis(type, v)];
    return (v.unites || []).map((u, rang) => ({
      ...actifDepuis(type, {
        ...u,
        // Chaque appareil emporte SA photo d'etiquette, celle qui porte son rang.
        photos_serial: (v.photos_serial || [])
          .filter((photo) => photo.label === etiquettePhotoRadiateur(rang)),
      }),
      nombre_radiateurs: 1,
    }));
  });

  const handleSign = async () => {
    setError(''); setSigning(true);
    try {
      const allAssets = buildAssetsPayload();
      const [first, ...rest] = allAssets;
      const position = await capturerPosition();
      const res = await api.post('/installateur/sign-complete', {
        geolocation: position,
        client: client,
        asset: first,
        extra_assets: rest,
        consents: consents,
        // UNE SEULE SIGNATURE, ET LE CHAMP `signature_attestation` NE PART PLUS.
        //
        // Il n a pas disparu du serveur pour autant : le canal QR l envoie encore, et
        // les dossiers signes entre le 27 et le 30 aout en portent une DISTINCTE.
        // Ne rien envoyer fait retomber le serveur sur celle-ci — c est la meme, et il
        // ecrit `signature_portee` pour le dire. Envoyer deux fois la meme valeur
        // aurait laisse croire a deux gestes du client.
        signature_data: signatureData,
        satisfaction: satisfaction,
        photos: photos,
        // Le choix du Pack, et sa signature. Le serveur s arrete avant le mandat quand
        // c est un refus : il n y a alors ni mandat a signer ni courriel a envoyer.
        offre: offre || OFFRE_ADHESION,
        agregateur: agregateur || null,
      });
      setResult(res.data);
      // Le consentement PDL part APRES la signature : il lui faut l'identifiant du
      // client, qui n'existe qu'une fois la fiche creee. Son echec ne fait jamais
      // echouer le parcours, la signature etant irrattrapable une fois l'installateur
      // reparti ; le dossier reste alors a recueillir depuis la fiche client.
      // Il part quel que soit le sort de la signature electronique : le client a donne
      // son accord en direct, cela ne depend pas de la reception d'un code.
      const clientId = res.data?.client_id || res.data?.client?.id;
      // ⚠️ IL Y AVAIT UNE CASE DEVANT CETTE CONDITION, ET ELLE A ETE RETIREE LE
      // 10/09/2026. NE PAS LA REMETTRE SANS RELIRE LA DECISION.
      //
      // La condition etait `consentPdl && clientId && client.pdl_prm` : le parcours
      // n'envoyait l'accord que si le client avait coche la carte Energrid. Demande de
      // Boubacar : « il n'est pas necessaire de garder la case a cocher pour le
      // consentement d'Energrid […] elle doit sauter de l'ecran de consentement avant
      // signature des mandats ». L'ecran ne demande donc plus rien, et le parcours envoie
      // l'accord SEUL.
      //
      // CE QUI PORTE LA PREUVE MAINTENANT, parce que ce n'est plus la case : le mandat
      // signe sur LE MEME ECRAN, quelques centimetres plus bas, dont l'autorisation
      // `donnees` couvre la collecte des donnees contractuelles et de consommation par le
      // gestionnaire de reseau et leur transmission a l'operateur. Energrid est le
      // prestataire technique par lequel cette collecte passe.
      //
      // ⚠️ ET CE QU'ELLE NE COUVRE PAS, mesure du 10/09 : le mandat ne nomme Energrid
      // nulle part, ne mentionne pas l'historique RETROSPECTIF de douze mois que la
      // declaration demande (`HISTORIQUE_MOIS_DEFAUT`), et le canal terrain ne fige ni la
      // version ni l'empreinte du texte signe -- seul `services/signature_distante.py` le
      // fait. L'ecart est CONNU, il est remonte, il n'est pas referme ici.
      // Tout est ecrit dans `docs/decisions/2026-09-10-retrait-de-la-clause-energrid.md`.
      if (clientId && client.pdl_prm) {
        try {
          await api.post('/pdl/consentements', {
            client_id: clientId,
            pdl_reference: client.pdl_prm,
            code_postal: client.code_postal,
            // Ecrit par le code, plus par une case. L'API continue de l'exiger, et c'est
            // voulu : voir `backend/app/modules/pdl/routes.py`.
            consentement_accepte: true,
          });
        } catch (e) {
          console.warn('consentement PDL non enregistre', e);
        }
      }
      // LE PARCOURS SE CONCLUT ICI, ET C'EST NOUVEAU DEPUIS LE 03/09/2026.
      //
      // Il ne se concluait pas : selon le canal, le client devait encore dicter un code
      // ou signer le lien recu par courriel, et l'ecran suivant etait une ATTENTE. Le
      // canal distant a ete retire — le client vient de parapher l'ecran, la signature
      // est acquise, il n'y a plus rien a attendre.
      //
      // Un seul aiguillage donc, et il ne depend plus du canal : on va a la
      // confirmation. Une signature qui n'a pas pu se conclure (PDF indisponible,
      // paraphe absent) n'arrete pas le chantier pour autant — le dossier existe, il
      // est non probant, et la confirmation le dit.
      setStep(5);
    } catch (err) {
      const detail = err.response?.data?.detail; refusNumero.retenir(err);
      setError(typeof detail === 'string' ? detail : (offre === OFFRE_ADHESION ? 'Erreur lors de la signature' : "Erreur lors de l'envoi du chantier"));
    }
    setSigning(false);
  };

  const resetForm = () => {
    setOffre(null);
    setStep(1);
    // `setConsentPdl(false)` etait remis a zero ici. L'etat a disparu avec la carte de
    // consentement Energrid le 10/09 : il n'y a plus rien a reinitialiser.
    setClient({ ...EMPTY_CLIENT });
    setEquipements({
      PV: { ...EQUIPEMENTS_INIT.PV },
      Batterie: { ...EQUIPEMENTS_INIT.Batterie },
      Ballon: { ...EQUIPEMENTS_INIT.Ballon },
      PAC: { ...EQUIPEMENTS_INIT.PAC },
      Radiateur: radiateurVierge(),
    });
    setReferenceChantier('');
    setConsents({ ...EMPTY_CONSENTS });
    setAgregateur('');
    setRecherchePdl(false);
    setSignatureData('');
    setResult(null); setError('');
    setSatisfaction({ chantier_propre: { answer: '', comment: '' }, equipe_pose: { answer: '', comment: '' }, emplacement: { answer: '', comment: '' }, satisfait_installation: { answer: '', comment: '' } });
    setPhotos([]);
  };

  const handlePhotoAdd = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 3 - photos.length;
    const toProcess = files.slice(0, remaining);
    const compressed = [];
    for (const f of toProcess) {
      try {
        const item = await compressImage(f);
        compressed.push(item);
      } catch {
        setError('Impossible de traiter une des photos');
      }
    }
    setPhotos(p => [...p, ...compressed].slice(0, 3));
    e.target.value = '';
  };

  const removePhoto = (idx) => {
    setPhotos(p => p.filter((_, i) => i !== idx));
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      await api.put('/auth/me/profile', profile);
      setProfileOpen(false);
    } catch {
      /* silent */
    }
    setProfileSaving(false);
  };

  const enregistrerPoseur = async () => {
    if (!poseurDialog) return;
    setPoseurSaving(true);
    try {
      const { mode, data } = poseurDialog;
      if (mode === 'create') {
        await api.post('/installateur/poseurs', data);
        toast.success(`Poseur ${data.prenom} ${data.nom} créé`);
      } else {
        const { id, ...corps } = data;
        await api.put(`/installateur/poseurs/${id}`, corps);
        toast.success('Poseur mis à jour');
      }
      setPoseurDialog(null);
      await rechargerPoseurs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setPoseurSaving(false);
  };

  // Desactivation, reactivation et suppression partagent la meme confirmation : ce sont
  // les trois gestes qui touchent l'acces d'un collegue, potentiellement en plein
  // chantier chez un client.
  const confirmerGestePoseur = async () => {
    if (!poseurConfirm) return;
    const { poseur, geste } = poseurConfirm;
    setPoseurConfirm(null);
    try {
      if (geste === 'supprimer') {
        await api.delete(`/installateur/poseurs/${poseur.id}`);
        toast.success(`Poseur supprimé : ${poseur.name}`);
      } else {
        await api.put(`/installateur/poseurs/${poseur.id}/toggle-active`);
        toast.success(`${poseur.active ? 'Désactivé' : 'Réactivé'} : ${poseur.name}`);
      }
      await rechargerPoseurs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const ouvrirCreationPoseur = () => setPoseurDialog({
    mode: 'create', data: { prenom: '', nom: '', email: '', password: '' },
  });

  // Ou le mandat a-t-il ete signe ? Sa version capture la position a cet instant
  // (InstallateurPage.js:431). Elle atteste que le poseur etait sur place.
  //
  // Trois refus possibles, tous silencieux et tous acceptables : pas de navigateur
  // geolocalisable, l'utilisateur refuse, ou l'appareil met trop longtemps. Dans les
  // trois cas on rend `null` et la signature aboutit — un chantier ne doit pas echouer
  // parce qu'un telephone n'a pas trouve les satellites.
  const capturerPosition = () => new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null);
      return;
    }
    const minuteur = setTimeout(() => resolve(null), 8000);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(minuteur);
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          captured_at: new Date().toISOString(),
        });
      },
      () => { clearTimeout(minuteur); resolve(null); },
      { enableHighAccuracy: true, timeout: 7000, maximumAge: 60000 },
    );
  });

  const handleLogout = () => { logout(); window.location.href = '/login'; };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="installateur-page">
      {/* Navigation : barre d'en-tete sur ecran large, onglets fixes en bas sur mobile.
          Le detail du pourquoi vit dans le composant. */}
      <NavigationInstallateur
        view={view}
        setView={setView}
        isPoseur={isPoseur}
        user={user}
        t={t}
        langue={langue}
        changerLangue={changerLangue}
        onProfil={() => setProfileOpen(true)}
        onLogout={handleLogout}
      />

      {/* Steps (wizard uniquement) - barre de progression visuelle */}
      {view === 'wizard' && (
        <div className="px-4 py-4 bg-white border-b border-neutral-100">
          <div className="max-w-md mx-auto">
            {/* Etape actuelle + pourcentage */}
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider">
                {t('lbl_step_progress', 'Étape')} {Math.min(step, STEPS.length)}/{STEPS.length} — {STEPS[Math.min(step, STEPS.length) - 1]?.label}
              </span>
              <span className="text-[11px] font-semibold text-marque" data-testid="wizard-progress-pct">
                {Math.round((Math.min(step - 1, STEPS.length) / STEPS.length) * 100)}%
              </span>
            </div>
            {/* Barre de progression segmentee */}
            <div className="flex gap-1.5 mb-3" data-testid="wizard-progress-bar">
              {STEPS.map((s) => {
                const reached = step >= s.id;
                const current = step === s.id;
                return (
                  <div
                    key={s.id}
                    className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                      current ? 'bg-marque' : reached ? 'bg-marque' : 'bg-neutral-200'
                    }`}
                    data-testid={`progress-segment-${s.id}`}
                  />
                );
              })}
            </div>
            {/* Pastilles avec icones */}
            <div className="flex items-center justify-between">
              {STEPS.map((s) => (
                <div key={s.id} className="flex flex-col items-center gap-1 flex-1">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold transition-colors ${
                    step > s.id ? 'bg-emerald-500 text-white' :
                    step === s.id ? 'bg-marque text-white' :
                    'bg-neutral-200 text-neutral-500'
                  }`}>
                    {step > s.id ? <Check className="w-3.5 h-3.5" /> : s.id}
                  </div>
                  <span className={`text-[10px] font-medium hidden sm:inline ${step >= s.id ? 'text-neutral-700' : 'text-neutral-400'}`}>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* `pb-28` sur mobile : la barre d'onglets est fixee en bas, sans cette reserve
          elle recouvre le dernier element de chaque vue. */}
      <div className="max-w-lg mx-auto p-4 pb-28 sm:pb-12">
        {view === 'home' && isPoseur && (
          <AccueilPoseur
            stats={poseurStats}
            user={user}
            t={t}
            onNouveauChantier={() => { setStep(1); setView('wizard'); }}
            onVoirChantiers={() => setView('mes-chantiers')}
          />
        )}

        {view === 'home' && !isPoseur && (
          <AccueilInstallateur
            stats={homeStats}
            user={user}
            t={t}
            onCreerPoseur={ouvrirCreationPoseur}
            onVoirChantiers={() => setView('mes-chantiers')}
            onVoirEquipe={() => setView('equipe')}
          />
        )}

        {view === 'equipe' && !isPoseur && (
          <MonEquipe
            poseurs={poseurs}
            t={t}
            onCreer={ouvrirCreationPoseur}
            onModifier={(p) => setPoseurDialog({
              mode: 'edit',
              data: { id: p.id, prenom: p.prenom || '', nom: p.nom || '', email: p.email, password: '' },
            })}
            onBasculer={(p) => setPoseurConfirm({ poseur: p, geste: 'basculer' })}
            onSupprimer={(p) => setPoseurConfirm({ poseur: p, geste: 'supprimer' })}
          />
        )}

        {/* Vue Mes Chantiers */}
        {view === 'mes-chantiers' && (
          <MesChantiers
            t={t}
            chantiers={chantiers}
            chantiersLoading={chantiersLoading}
            chantierSearch={chantierSearch}
            setChantierSearch={setChantierSearch}
            chantiersFiltered={chantiersFiltered}
            fmtDate={fmtDate}
            downloadAttestation={downloadAttestation}
          />
        )}

        {/* Vue wizard (chantier en cours) */}
        {view === 'wizard' && (<>
          {step === 1 && (
            <StepClientInfo
              t={t}
              client={client}
              up={up}
              pdlError={pdlError}
              menuData={menuData}
              step1Valid={step1Valid}
              setStep={setStep}
            />
          )}

          {step === 2 && (
            <StepEquipement
              t={t}
              equipements={equipements}
              upE={upE}
              toggleE={toggleE}
              referenceChantier={referenceChantier}
              setReferenceChantier={setReferenceChantier}
              enabledEquipements={enabledEquipements}
              step2Valid={step2Valid} refus={refusNumero.refus}
              setStep={setStep}
            />
          )}

          {step === 3 && (
            <StepOffre
              offre={offre}
              setOffre={setOffre}
              setStep={setStep}
              valide={!!offre} error={error} signing={signing}
              onSuivant={() => {
                // L adhesion mene a la fin de chantier, ou tout se signe d un geste.
                // Le refus n a rien a signer de plus : le chantier se conclut sur l
                // enregistrement produit, et c est le serveur qui s arrete avant le
                // mandat. Il ne recueille donc AUCUN consentement — il n y a rien a
                // consentir quand il n y a pas de mandat.
                if (offre === OFFRE_ADHESION) setStep(4);
                else handleSign();
              }}
            />
          )}

          {/* Etape 4 — MANDAT, CONSENTEMENTS ET RECEPTION, sous UNE signature.
              Decision de David du 30/08, qui revoque son decoupage du 27/08.

              ⚠️ ELLE EST DESORMAIS LA DERNIERE ETAPE QUI ENGAGE. Le client signe au
              doigt, le serveur conclut dans le meme appel, et l'ecran suivant est la
              confirmation — plus une attente. La cloture est portee par la
              confirmation, seul endroit ou l'on sait que la signature est acquise. */}
          {step === 4 && (
            <StepFinChantier
              client={client}
              enabledEquipements={enabledEquipements}
              referenceChantier={referenceChantier}
              satisfaction={satisfaction}
              setSatisfaction={setSatisfaction}
              photos={photos}
              handlePhotoAdd={handlePhotoAdd}
              removePhoto={removePhoto}
              consents={consents}
              setConsents={setConsents}
              agregateur={agregateur} setAgregateur={setAgregateur}
              signature={signatureData} setSignature={setSignatureData}
              materielEnAttente={materielEnAttente}
              error={error}
              valide={step4Valid}
              signing={signing}
              handleSign={handleSign}
              setStep={setStep}
            />
          )}
          {step === 5 && result && (
            <StepConfirmation
              client={client}
              enabledEquipements={enabledEquipements}
              referenceChantier={referenceChantier}
              mandatId={result.mandat_id}
              signature={result.signature}
              resetForm={resetForm}
            />
          )}
        </>)}
      </div>

      {/* Mon profil dialog */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="max-w-md" data-testid="profile-dialog">
          <DialogHeader>
            <DialogTitle>Mon profil installateur</DialogTitle>
            <DialogDescription>Ces informations apparaîtront en en-tête des attestations de fin de travaux remises aux clients.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] text-neutral-500">Société</Label>
              <Input
                className="h-11 text-[14px]"
                value={profile.company_name}
                onChange={e => setProfile(p => ({ ...p, company_name: e.target.value }))}
                placeholder="Nom commercial de votre entreprise"
                data-testid="profile-company"
              />
            </div>
            <div>
              <Label className="text-[11px] text-neutral-500">Adresse</Label>
              <Input
                className="h-11 text-[14px]"
                value={profile.address}
                onChange={e => setProfile(p => ({ ...p, address: e.target.value }))}
                placeholder="Adresse complète"
                data-testid="profile-address"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[11px] text-neutral-500">Code postal</Label>
                <Input
                  className="h-11 text-[14px]"
                  value={profile.code_postal}
                  onChange={e => setProfile(p => ({ ...p, code_postal: e.target.value }))}
                  placeholder="75011"
                  inputMode="numeric"
                  data-testid="profile-code-postal"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[11px] text-neutral-500">Ville</Label>
                <Input
                  className="h-11 text-[14px]"
                  value={profile.ville}
                  onChange={e => setProfile(p => ({ ...p, ville: e.target.value }))}
                  placeholder="Paris"
                  data-testid="profile-ville"
                />
              </div>
            </div>
            <div>
              <Label className="text-[11px] text-neutral-500">Téléphone</Label>
              <Input
                className="h-11 text-[14px]"
                value={profile.phone}
                onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
                placeholder="06 XX XX XX XX"
                data-testid="profile-phone"
              />
            </div>
            <div>
              <Label className="text-[11px] text-neutral-500">Email (lecture seule)</Label>
              <Input className="h-9 bg-neutral-50" value={user?.email || ''} disabled />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileOpen(false)}>{t('btn_cancel', "Annuler")}</Button>
            <Button onClick={saveProfile} disabled={profileSaving} data-testid="profile-save-btn">
              {profileSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement...</> : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PoseurDialog
        etat={poseurDialog}
        enregistrement={poseurSaving}
        t={t}
        onChange={(champ) => setPoseurDialog(d => ({ ...d, data: { ...d.data, ...champ } }))}
        onFermer={() => setPoseurDialog(null)}
        onEnregistrer={enregistrerPoseur}
      />
      <ConfirmationPoseur
        etat={poseurConfirm}
        t={t}
        onFermer={() => setPoseurConfirm(null)}
        onConfirmer={confirmerGestePoseur}
      />
    </div>
  );
}
